import {
  CONTROL_MARKERS_TO_WIN,
  type GamePhase,
  getEliminatedUnits,
  listTacticCandidates,
  listTacticTargetHexes,
  type PlayerId,
  type TacticCandidate,
  validateAttack,
  validateBolster,
  validateControl,
  validateDeploy,
  validateMove,
  type WarchestAction,
  type WarchestState,
} from "@cloudnest/warchest-engine";
import { useEffect, useRef, useState } from "react";
import { navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { WarchestBoard } from "./components/WarchestBoard";
import { WarchestDraftPanel } from "./components/WarchestDraftPanel";
import { WarchestHistoryPanel } from "./components/WarchestHistoryPanel";
import { WarchestModeSelect } from "./components/WarchestModeSelect";
import { WarchestUnitCards } from "./components/WarchestUnitCards";
import { WarchestZonePanel } from "./components/WarchestZonePanel";
import { WC_FACTION_NAME, WC_UNIT_NAME } from "./i18n";
import {
  multiplayerGameActions,
  useMultiplayerGameStore,
} from "./multiplayer-game-store";

const FACTION_TOKEN: Record<PlayerId, string> = {
  P1: "var(--cn-warchest-raven)",
  P2: "var(--cn-warchest-wolf)",
};
const FACTION_NAME = WC_FACTION_NAME;
const CONNECTION_LABEL: Record<string, string> = {
  connected: "연결됨",
  reconnecting: "재연결 중",
  disconnected: "연결 끊김",
};
const PHASE_LABEL: Record<GamePhase, string> = {
  DRAFT: "준비",
  DRAW_COINS: "뽑기",
  USE_COINS: "플레이",
  GAME_END: "종료",
};

function countControl(gameState: WarchestState, player: PlayerId): number {
  let n = 0;
  for (const loc of Object.values(gameState.locations)) {
    if (loc.controlledBy === player) n += 1;
  }
  return n;
}

function ScoreChip({
  player,
  score,
  isInitiativeHolder,
}: {
  player: PlayerId;
  score: number;
  isInitiativeHolder: boolean;
}) {
  const color = FACTION_TOKEN[player];
  return (
    <div
      className="flex items-center gap-1.5 rounded-full px-3 py-1"
      style={{
        background: color,
        color: "var(--cn-warchest-card-bg)",
        boxShadow: isInitiativeHolder
          ? `0 0 0 2px var(--cn-warchest-selected)`
          : "none",
      }}
      data-testid={`warchest-score-${player}`}
    >
      <span className="font-bold text-sm">{FACTION_NAME[player]}</span>
      <span className="font-mono text-xs">
        {score}/{CONTROL_MARKERS_TO_WIN}
      </span>
    </div>
  );
}

function ActivePlayerChip({
  player,
  isMe,
}: {
  player: PlayerId;
  isMe: boolean;
}) {
  return (
    <div
      className="flex items-center gap-1.5 rounded-full px-3 py-1.5"
      style={{
        background: FACTION_TOKEN[player],
        color: "var(--cn-warchest-card-bg)",
      }}
      data-testid="warchest-active-player"
    >
      <span
        className="h-2 w-2 rounded-full"
        style={{ background: "var(--cn-warchest-card-bg)" }}
      />
      <span className="font-semibold text-sm">
        {isMe ? "내 차례" : `${FACTION_NAME[player]} 차례`}
      </span>
    </div>
  );
}

function TurnBanner({
  activePlayer,
  isMe,
}: {
  activePlayer: PlayerId;
  isMe: boolean;
}) {
  return (
    <div
      data-testid="warchest-turn-banner"
      data-active={isMe ? "me" : "opponent"}
      className="flex items-center justify-center gap-2 rounded-lg px-4 py-3"
      style={{
        background: isMe
          ? FACTION_TOKEN[activePlayer]
          : "var(--cn-warchest-card-bg)",
        border: isMe
          ? "1px solid transparent"
          : `2px solid ${FACTION_TOKEN[activePlayer]}`,
        color: isMe ? "var(--cn-warchest-card-bg)" : "var(--cn-warchest-text)",
      }}
    >
      <span className="font-bold text-base sm:text-lg">
        {isMe ? "내 차례" : `${FACTION_NAME[activePlayer]} 차례`}
      </span>
      {!isMe && (
        <span className="font-medium text-sm" style={{ opacity: 0.7 }}>
          · 기다리는 중…
        </span>
      )}
    </div>
  );
}

// Design-ref top bar: a turn-order crest sits between the two score chips,
// tinted with the initiative holder's faction color. Purely additive — the
// round/phase badges keep their own testids.
function TurnOrderCrest({
  initiativeHolder,
  turn,
}: {
  initiativeHolder: PlayerId;
  turn: number;
}) {
  return (
    <div
      aria-hidden="true"
      data-testid="warchest-turn-crest"
      data-initiative={initiativeHolder}
      title={`${FACTION_NAME[initiativeHolder]} 선공권 · ${turn}라운드`}
      className="flex h-7 w-7 flex-none items-center justify-center rounded-full font-bold text-xs"
      style={{
        background: "var(--cn-warchest-card-bg)",
        border: `2px solid ${FACTION_TOKEN[initiativeHolder]}`,
        color: FACTION_TOKEN[initiativeHolder],
      }}
    >
      ⚔
    </div>
  );
}

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const {
    gameState,
    myPlayerId,
    selectedCoin,
    legalTargets,
    lastError,
    events,
    needsHostSetup,
  } = useMultiplayerGameStore();
  const actions = multiplayerGameActions;

  const roomId = currentRoom?.id ?? null;
  const isHost = currentRoom?.hostUserId === user?.id;

  const [selectedHex, setSelectedHex] = useState<string | null>(null);
  const [pickerHex, setPickerHex] = useState<string | null>(null);
  const [showCards, setShowCards] = useState(false);

  const actionsRef = useRef(actions);
  const lobbyActionsRef = useRef(lobbyActions);
  useEffect(() => {
    actionsRef.current = actions;
    lobbyActionsRef.current = lobbyActions;
  });

  const { connectionStatus } = useGameEventsWS(roomId, {
    onGameEvent: (eventType, payload, seq) => {
      actionsRef.current.applyRemoteEvent(eventType, payload, seq);
    },
    onViewerState: (viewerState, seq) => {
      actionsRef.current.applyRemoteViewerState(viewerState, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: currentRoom.participantUserIds is intentionally omitted — every game:room_updated creates a new array reference which would re-run reset()+init and scramble game state. participantUserIds is stable after game start.
  useEffect(() => {
    if (!roomId) return;
    if (isHost) {
      const playerIds = currentRoom?.participantUserIds ?? [];
      void actions.initializeAsHost(roomId, playerIds);
    } else {
      const playerIds = currentRoom?.participantUserIds ?? [];
      void actions.initializeAsGuest(roomId, playerIds);
    }
    return () => {
      actions.reset();
    };
  }, [roomId, isHost]);

  if (!gameState) {
    // Host pre-game mode screen — carries the standard `game-screen` testid so
    // the cross-game "game is up" gate passes while the host is choosing.
    if (isHost && needsHostSetup) {
      return (
        <div
          className="flex min-h-screen flex-col"
          style={{
            background: "var(--cn-warchest-bg)",
            color: "var(--cn-warchest-text)",
          }}
          data-testid="game-screen"
        >
          <WarchestModeSelect
            onSelect={(mode) => {
              void actions.submitInit(mode);
            }}
          />
        </div>
      );
    }
    return (
      <div
        data-testid="warchest-loading"
        className="p-6 text-center"
        style={{
          background: "var(--cn-warchest-bg)",
          color: "var(--cn-warchest-text)",
          minHeight: "100vh",
        }}
      >
        워체스트 불러오는 중…
      </div>
    );
  }

  const isMyTurn = gameState.activePlayer === myPlayerId;
  const isGameOver = gameState.phase === "GAME_END";
  const isDraft = gameState.phase === "DRAFT";

  function handleDraftPick(unitType: string) {
    if (!myPlayerId || !isMyTurn) return;
    void actions.submitAction({
      type: "DRAFT_PICK",
      player: myPlayerId,
      unitType,
    });
  }

  function computeTargets(coinType: string): string[] {
    if (!gameState || !myPlayerId) return [];
    const player = myPlayerId;
    const boardDef = gameState.config.boards[gameState.boardId];
    if (!boardDef) return [];
    const targets = new Set<string>();
    for (const hex of boardDef.hexes) {
      if (
        validateDeploy(gameState, {
          type: "DEPLOY",
          player,
          coinType,
          targetHex: hex,
        }).valid
      )
        targets.add(hex);
      if (
        validateControl(gameState, {
          type: "CONTROL",
          player,
          coinType,
          targetHex: hex,
        }).valid
      )
        targets.add(hex);
      for (const toHex of boardDef.adjacency[hex] ?? []) {
        if (
          validateMove(gameState, {
            type: "MOVE",
            player,
            coinType,
            fromHex: hex,
            toHex,
          }).valid
        )
          targets.add(toHex);
        if (
          validateAttack(gameState, {
            type: "ATTACK",
            player,
            coinType,
            attackerHex: hex,
            targetHex: toHex,
          }).valid
        )
          targets.add(toHex);
      }
    }
    for (const [hex, cell] of Object.entries(gameState.board)) {
      if (
        validateBolster(gameState, {
          type: "BOLSTER",
          player,
          coinType,
          targetHex: hex,
        }).valid &&
        cell
      )
        targets.add(hex);
    }
    for (const hex of listTacticTargetHexes(gameState, player, coinType)) {
      targets.add(hex);
    }
    return [...targets];
  }

  function handleCoinSelect(coinType: string | null) {
    if (!isMyTurn) return;
    actions.selectCoin(coinType);
    setSelectedHex(null);
    setPickerHex(null);
  }

  function handleHexClick(hex: string) {
    if (!isMyTurn || !selectedCoin) return;
    setSelectedHex(hex);
    setPickerHex(hex);
  }

  async function handleAction(action: WarchestAction) {
    setPickerHex(null);
    setSelectedHex(null);
    actions.selectCoin(null);
    await actions.submitAction(action);
  }

  const computedTargets =
    selectedCoin && isMyTurn ? computeTargets(selectedCoin) : legalTargets;

  const tacticActions =
    selectedCoin && pickerHex && myPlayerId
      ? listTacticCandidates(gameState, myPlayerId, selectedCoin).filter(
          (candidate) =>
            candidate.primaryHex === pickerHex ||
            candidate.involvedHexes.includes(pickerHex),
        )
      : [];

  // Determine layout — opponent tray on top, self tray on bottom.
  const opponentId: PlayerId | null =
    myPlayerId === "P1" ? "P2" : myPlayerId === "P2" ? "P1" : null;

  return (
    <div
      className="min-h-screen p-2 sm:p-4"
      style={{
        background: "var(--cn-warchest-bg)",
        color: "var(--cn-warchest-text)",
      }}
      data-testid="game-screen"
    >
      <div className="mx-auto w-full max-w-4xl space-y-2 px-2 sm:space-y-3 sm:px-0">
        <header
          className="flex items-center justify-between rounded-xl px-4 py-2.5"
          style={{
            background: "var(--cn-warchest-card-bg)",
            border: "1px solid var(--cn-warchest-card-border)",
          }}
          data-testid="warchest-top-bar"
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="game-leave-button"
              onClick={() => {
                actions.reset();
                navigateTo("/warchest/lobby");
              }}
              className="rounded-lg px-3 py-1.5 font-medium text-sm"
              style={{
                background: "transparent",
                border: "1px solid var(--cn-warchest-card-border)",
                color: "var(--cn-warchest-text)",
              }}
            >
              ← 나가기
            </button>
            <button
              type="button"
              data-testid="warchest-unit-cards-button"
              onClick={() => setShowCards(true)}
              className="rounded-lg px-3 py-1.5 font-medium text-sm"
              style={{
                background: "transparent",
                border: "1px solid var(--cn-warchest-card-border)",
                color: "var(--cn-warchest-text)",
              }}
            >
              유닛 카드
            </button>
            {!isGameOver && (
              <ActivePlayerChip
                player={gameState.activePlayer}
                isMe={isMyTurn}
              />
            )}
            <span
              data-testid="connection-status"
              data-status={connectionStatus}
              className="rounded-full px-2 py-0.5 font-medium text-xs"
              style={{
                background:
                  connectionStatus === "connected"
                    ? "var(--cn-warchest-valid-deploy)"
                    : "var(--cn-warchest-attack)",
                color:
                  connectionStatus === "connected"
                    ? "var(--cn-warchest-text)"
                    : "var(--cn-warchest-card-bg)",
              }}
            >
              {CONNECTION_LABEL[connectionStatus] ?? connectionStatus}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ScoreChip
              player="P1"
              score={countControl(gameState, "P1")}
              isInitiativeHolder={gameState.initiativeHolder === "P1"}
            />
            <TurnOrderCrest
              initiativeHolder={gameState.initiativeHolder}
              turn={gameState.turn}
            />
            <span
              data-testid="warchest-round"
              data-round={gameState.turn}
              className="font-mono text-xs"
              style={{ color: "var(--cn-warchest-text-muted)" }}
            >
              R{gameState.turn}
            </span>
            <span
              data-testid="warchest-phase-badge"
              data-phase={gameState.phase}
              className="rounded-full px-2 py-0.5 font-semibold text-xs"
              style={{
                background: "var(--cn-warchest-card-border)",
                color: "var(--cn-warchest-text)",
              }}
            >
              {PHASE_LABEL[gameState.phase]}
            </span>
            <ScoreChip
              player="P2"
              score={countControl(gameState, "P2")}
              isInitiativeHolder={gameState.initiativeHolder === "P2"}
            />
          </div>
        </header>

        {!isGameOver && (
          <TurnBanner activePlayer={gameState.activePlayer} isMe={isMyTurn} />
        )}

        {lastError && (
          <div
            className="rounded-md px-4 py-2 text-sm"
            style={{
              background: "var(--cn-warchest-attack)",
              color: "var(--cn-warchest-card-bg)",
            }}
          >
            {lastError}
          </div>
        )}

        {isGameOver && gameState.winner && (
          <div
            data-testid="warchest-game-over"
            data-winner={gameState.winner}
            className="rounded-xl p-4 text-center"
            style={{
              background: "var(--cn-warchest-card-bg)",
              border: `2px solid ${FACTION_TOKEN[gameState.winner]}`,
            }}
          >
            <div
              data-testid="warchest-winner-name"
              className="font-bold text-xl"
              style={{ color: FACTION_TOKEN[gameState.winner] }}
            >
              {gameState.winner === myPlayerId
                ? "승리!"
                : `${FACTION_NAME[gameState.winner]} 승리!`}
            </div>
            <button
              type="button"
              data-testid="warchest-back-to-lobby"
              onClick={() => {
                actions.reset();
                navigateTo("/warchest/lobby");
              }}
              className="mt-3 rounded-lg px-6 py-2 font-semibold"
              style={{
                background: FACTION_TOKEN[gameState.winner],
                color: "var(--cn-warchest-card-bg)",
              }}
            >
              로비로
            </button>
          </div>
        )}

        {isDraft && (
          <WarchestDraftPanel
            gameState={gameState}
            myPlayerId={myPlayerId}
            onPick={handleDraftPick}
          />
        )}

        {!isDraft && (
          <>
            {/* Opponent tray (top) */}
            {opponentId && (
              <WarchestZonePanel
                player={opponentId}
                zones={gameState.playerZones[opponentId]}
                isActive={gameState.activePlayer === opponentId}
                isInitiativeHolder={gameState.initiativeHolder === opponentId}
                isMe={false}
                selectedCoin={null}
                onCoinSelect={() => {}}
              />
            )}

            {/* Board frame */}
            <div
              className="rounded-2xl p-3"
              style={{
                background: "var(--cn-warchest-card-bg)",
                border: "1px solid var(--cn-warchest-card-border)",
              }}
              data-testid="warchest-board-frame"
            >
              <WarchestBoard
                gameState={gameState}
                legalTargets={computedTargets}
                selectedHex={selectedHex}
                onHexClick={handleHexClick}
                disabled={!isMyTurn}
              />
            </div>

            {/* Self tray (bottom) */}
            {myPlayerId && (
              <WarchestZonePanel
                player={myPlayerId}
                zones={gameState.playerZones[myPlayerId]}
                isActive={isMyTurn}
                isInitiativeHolder={gameState.initiativeHolder === myPlayerId}
                isMe={true}
                selectedCoin={selectedCoin}
                onCoinSelect={handleCoinSelect}
                eliminatedUnits={getEliminatedUnits(gameState, myPlayerId)}
              />
            )}

            {/* Action history (collapsible, read-only) */}
            <WarchestHistoryPanel events={events} />

            {/* Action panel — pins to viewport bottom as a sheet on phones (A4);
            inline on tablet/desktop. Safe-area padding clears the iOS home
            indicator. Existing `warchest-action-panel` testid preserved on the
            inner element for backward compat. */}
            {isMyTurn && selectedCoin && myPlayerId && (
              <div
                data-testid="warchest-action-panel-sheet"
                className="fixed inset-x-0 bottom-0 z-10 px-2 pb-[env(safe-area-inset-bottom)] sm:static sm:inset-auto sm:z-auto sm:px-0 sm:pb-0"
              >
                <div
                  className="rounded-lg p-3 shadow-lg sm:shadow-none"
                  style={{
                    background: "var(--cn-warchest-card-bg)",
                    border: "1px solid var(--cn-warchest-card-border)",
                  }}
                  data-testid="warchest-action-panel"
                >
                  <div
                    className="mb-2 text-xs"
                    style={{ color: "var(--cn-warchest-text-muted)" }}
                  >
                    선택:{" "}
                    <strong>
                      {WC_UNIT_NAME[selectedCoin] ?? selectedCoin}
                    </strong>
                    {pickerHex && <span> — 헥스 {pickerHex}</span>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {pickerHex &&
                      validateDeploy(gameState, {
                        type: "DEPLOY",
                        player: myPlayerId,
                        coinType: selectedCoin,
                        targetHex: pickerHex,
                      }).valid && (
                        <ActionButton
                          testId="warchest-action-deploy"
                          label={`${pickerHex}에 배치`}
                          factionTone="raven"
                          onClick={() =>
                            handleAction({
                              type: "DEPLOY",
                              player: myPlayerId as PlayerId,
                              coinType: selectedCoin,
                              targetHex: pickerHex as string,
                            })
                          }
                        />
                      )}
                    {pickerHex &&
                      validateBolster(gameState, {
                        type: "BOLSTER",
                        player: myPlayerId,
                        coinType: selectedCoin,
                        targetHex: pickerHex,
                      }).valid && (
                        <ActionButton
                          testId="warchest-action-bolster"
                          label={`${pickerHex} 강화`}
                          factionTone="muted"
                          onClick={() =>
                            handleAction({
                              type: "BOLSTER",
                              player: myPlayerId as PlayerId,
                              coinType: selectedCoin,
                              targetHex: pickerHex as string,
                            })
                          }
                        />
                      )}
                    {pickerHex &&
                      validateControl(gameState, {
                        type: "CONTROL",
                        player: myPlayerId,
                        coinType: selectedCoin,
                        targetHex: pickerHex,
                      }).valid && (
                        <ActionButton
                          testId="warchest-action-control"
                          label={`${pickerHex} 점령`}
                          factionTone="selected"
                          onClick={() =>
                            handleAction({
                              type: "CONTROL",
                              player: myPlayerId as PlayerId,
                              coinType: selectedCoin,
                              targetHex: pickerHex as string,
                            })
                          }
                        />
                      )}
                    <ActionButton
                      testId="warchest-action-pass"
                      label="패스"
                      factionTone="muted"
                      onClick={() =>
                        handleAction({
                          type: "PASS",
                          player: myPlayerId as PlayerId,
                          coinType: selectedCoin,
                        })
                      }
                    />
                    <ActionButton
                      testId="warchest-action-claim-initiative"
                      label="선공권 획득"
                      factionTone="selected"
                      onClick={() =>
                        handleAction({
                          type: "CLAIM_INITIATIVE",
                          player: myPlayerId as PlayerId,
                          coinType: selectedCoin,
                        })
                      }
                    />
                    {gameState.playerZones[myPlayerId].supply.length > 0 && (
                      <select
                        defaultValue=""
                        onChange={(e) => {
                          if (e.target.value)
                            handleAction({
                              type: "RECRUIT",
                              player: myPlayerId as PlayerId,
                              coinType: selectedCoin,
                              recruitCoinType: e.target.value,
                            });
                        }}
                        className="rounded-md px-2 py-1.5 text-sm"
                        style={{
                          background: "var(--cn-warchest-card-bg)",
                          border: "1px solid var(--cn-warchest-card-border)",
                          color: "var(--cn-warchest-text)",
                        }}
                      >
                        <option value="" disabled>
                          모집…
                        </option>
                        {[
                          ...new Set(gameState.playerZones[myPlayerId].supply),
                        ].map((c) => (
                          <option key={c} value={c}>
                            {WC_UNIT_NAME[c] ?? c}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  {tacticActions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {tacticActions.map((candidate) => (
                        <ActionButton
                          key={JSON.stringify(candidate.action)}
                          testId="warchest-action-tactic"
                          label={formatTacticLabel(candidate)}
                          factionTone="selected"
                          onClick={() => handleAction(candidate.action)}
                        />
                      ))}
                    </div>
                  )}
                  {pickerHex && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {gameState.config.boards[gameState.boardId]?.hexes.map(
                        (fromHex) => {
                          if (
                            !validateMove(gameState, {
                              type: "MOVE",
                              player: myPlayerId as PlayerId,
                              coinType: selectedCoin,
                              fromHex,
                              toHex: pickerHex as string,
                            }).valid
                          )
                            return null;
                          return (
                            <ActionButton
                              key={`move-${fromHex}`}
                              testId={`warchest-action-move-${fromHex}-${pickerHex}`}
                              label={`${fromHex} → ${pickerHex} 이동`}
                              factionTone="raven"
                              onClick={() =>
                                handleAction({
                                  type: "MOVE",
                                  player: myPlayerId as PlayerId,
                                  coinType: selectedCoin,
                                  fromHex,
                                  toHex: pickerHex as string,
                                })
                              }
                            />
                          );
                        },
                      )}
                      {gameState.config.boards[gameState.boardId]?.hexes.map(
                        (attackerHex) => {
                          if (
                            !validateAttack(gameState, {
                              type: "ATTACK",
                              player: myPlayerId as PlayerId,
                              coinType: selectedCoin,
                              attackerHex,
                              targetHex: pickerHex as string,
                            }).valid
                          )
                            return null;
                          return (
                            <ActionButton
                              key={`atk-${attackerHex}`}
                              testId={`warchest-action-attack-${attackerHex}-${pickerHex}`}
                              label={`${attackerHex}에서 ${pickerHex} 공격`}
                              factionTone="attack"
                              onClick={() =>
                                handleAction({
                                  type: "ATTACK",
                                  player: myPlayerId as PlayerId,
                                  coinType: selectedCoin,
                                  attackerHex,
                                  targetHex: pickerHex as string,
                                })
                              }
                            />
                          );
                        },
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {showCards && <WarchestUnitCards onClose={() => setShowCards(false)} />}
      </div>
    </div>
  );
}

type ActionTone = "raven" | "muted" | "selected" | "attack";

const TONE_BG: Record<ActionTone, string> = {
  raven: "var(--cn-warchest-valid-deploy)",
  muted: "transparent",
  selected: "var(--cn-warchest-selected)",
  attack: "var(--cn-warchest-attack)",
};

const TONE_FG: Record<ActionTone, string> = {
  raven: "var(--cn-warchest-text)",
  muted: "var(--cn-warchest-text)",
  selected: "var(--cn-warchest-text)",
  attack: "var(--cn-warchest-card-bg)",
};

function ActionButton({
  label,
  factionTone,
  onClick,
  testId,
}: {
  label: string;
  factionTone: ActionTone;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="rounded-md px-3 py-1.5 text-sm"
      style={{
        background: TONE_BG[factionTone],
        color: TONE_FG[factionTone],
        border: "1px solid var(--cn-warchest-card-border)",
      }}
    >
      {label}
    </button>
  );
}

function readString(
  payload: Readonly<Record<string, unknown>>,
  key: string,
): string | null {
  const value = payload[key];
  return typeof value === "string" ? value : null;
}

function readStringArray(
  payload: Readonly<Record<string, unknown>>,
  key: string,
): readonly string[] {
  const value = payload[key];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function formatFootmanManeuver(value: unknown): string | null {
  if (typeof value !== "object" || value === null) return null;
  const item = value as Readonly<Record<string, unknown>>;
  const footmanHex = readString(item, "footmanHex");
  const targetHex = readString(item, "targetHex");
  const action = readString(item, "action");
  if (!footmanHex || !targetHex || !action) return null;
  const verb =
    action === "MOVE" ? "이동" : action === "ATTACK" ? "공격" : "점령";
  return `${footmanHex} ${verb} ${targetHex}`;
}

function formatTacticLabel(candidate: TacticCandidate): string {
  const { action } = candidate;
  const unitName = WC_UNIT_NAME[action.coinType] ?? action.coinType;
  const payload = action.tacticPayload;

  if (action.coinType === "ARCHER" || action.coinType === "CROSSBOWMAN") {
    return `${unitName} 전술: ${readString(payload, "targetHex")} 공격`;
  }
  if (action.coinType === "BERSERKER") {
    return `${unitName} 전술: ${readStringArray(payload, "moves").join(" → ")} 돌진`;
  }
  if (action.coinType === "CAVALRY") {
    return `${unitName} 전술: ${readString(payload, "toHex")} 이동 후 ${readString(payload, "attackHex")} 공격`;
  }
  if (action.coinType === "ENSIGN") {
    return `${unitName} 전술: ${readString(payload, "friendlyHex")} → ${readString(payload, "toHex")} 집결`;
  }
  if (action.coinType === "FOOTMAN") {
    const maneuvers = Array.isArray(payload.maneuvers)
      ? payload.maneuvers.map(formatFootmanManeuver).filter(Boolean)
      : [];
    return `${unitName} 전술: ${maneuvers.join(" / ")}`;
  }
  if (action.coinType === "LANCER") {
    return `${unitName} 전술: ${readStringArray(payload, "path").join(" → ")} 이동 후 ${readString(payload, "attackHex")} 공격`;
  }
  if (action.coinType === "LIGHT_CAVALRY") {
    return `${unitName} 전술: ${readStringArray(payload, "path").join(" → ")} 이동`;
  }
  if (action.coinType === "MARSHALL") {
    return `${unitName} 전술: ${readString(payload, "friendlyHex")}에게 ${readString(payload, "targetHex")} 공격 명령`;
  }
  if (action.coinType === "ROYAL") {
    return `근위병 전술: ${action.unitHex} → ${readString(payload, "toHex")} 보호 이동`;
  }
  return `${unitName} 전술`;
}
